// Neighborhoods.jsx
import React, { useCallback, useMemo, useState } from "react";
import "../css/LandingPage.css";
import "../css/Neighborhoods.css";
import Navbar from "../Components/NavBar";
import SectionNav from "../Components/SectionNav";

const LISTINGS_BASE = "https://kentuckyhomeliving.kw.com/search/sale";
const KW_LOCATION_BASE = "https://kentuckyhomeliving.kw.com/search/location";

/* -----------------------------
   URL HELPERS
------------------------------ */
function openNewTab(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * ✅ Universal KW search URL
 * Works for ANY query (city, neighborhood, address, county, etc.)
 * If viewport exists, KW behaves like its own UI.
 */
function buildKwSearchUrl(q, viewport) {
  const query = String(q || "").trim();
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (viewport) params.set("viewport", viewport);

  // IMPORTANT: keep it stable (no "/?")
  return `${LISTINGS_BASE}?${params.toString()}`;
}

function buildKwEntityUrl({ slug, id, q, viewport }) {
  const base = `${LISTINGS_BASE}/${slug}/${id}`;
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (viewport) params.set("viewport", viewport);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function buildZipLocationUrl({ token, viewport, zoom }) {
  const params = new URLSearchParams();
  if (viewport) params.set("viewport", viewport);
  if (zoom) params.set("zoom", String(zoom));
  const qs = params.toString();
  return `${KW_LOCATION_BASE}/${token}${qs ? `?${qs}` : ""}`;
}

function normalizeQuery(input) {
  return String(input || "").trim();
}

/* -----------------------------
   VIEWPORT LOOKUP (universal)
------------------------------ */
/**
 * Preferred: backend viewport lookup (if available)
 * Expects: GET /api/kw/viewport?q=<place>
 * Returns: { viewport: "north,east,south,west", q?: "Formatted Place, ..." }
 */
async function fetchKwViewport(placeQuery) {
  try {
    const r = await fetch(`/api/kw/viewport?q=${encodeURIComponent(placeQuery)}`);
    if (!r.ok) return null;
    const data = await r.json();
    return {
      viewport: data?.viewport || null,
      q: data?.q || null,
    };
  } catch {
    return null;
  }
}

/**
 * Client fallback: Google Geocode
 * Bias results toward Northern KY / Cincinnati metro (without hard-locking KY)
 *
 * .env:
 * REACT_APP_GOOGLE_MAPS_API_KEY=xxxx
 */
async function getViewportFromGeocode(placeText) {
  const text = String(placeText || "").trim();
  if (!text) return null;

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return null;

  const address = encodeURIComponent(text);

  // Bias box (adjust if you want)
  const bounds = encodeURIComponent("38.55,-85.10|39.35,-84.20");
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&bounds=${bounds}&region=us&key=${key}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    const result = data?.results?.[0];
    const viewport = result?.geometry?.viewport;

    if (!viewport?.northeast || !viewport?.southwest) return null;

    const ne = viewport.northeast;
    const sw = viewport.southwest;

    return {
      viewport: `${ne.lat},${ne.lng},${sw.lat},${sw.lng}`, // KW wants N,E,S,W
      q: result?.formatted_address || text, // KW likes formatted addresses
    };
  } catch {
    return null;
  }
}

/* -----------------------------
   DATA
------------------------------ */
const FEATURED_CITIES = [
  { name: "Florence", blurb: "Convenient, growing, great access.", tag: "Popular" },
  { name: "Fort Thomas", blurb: "Historic charm + strong schools.", tag: "Schools" },
  { name: "Union", blurb: "Newer homes + family-friendly feel.", tag: "Families" },
  { name: "Covington", blurb: "Urban energy + walkable pockets.", tag: "City vibe" },
  { name: "Newport", blurb: "Riverfront living near Cincinnati.", tag: "Lifestyle" },
  { name: "Independence", blurb: "Space, value, and quiet neighborhoods.", tag: "Value" },
];

const LIFESTYLE_BUCKETS = [
  {
    title: "Family-Friendly Communities",
    desc: "Space, parks, and day-to-day convenience — great for settling in.",
    pills: ["Union", "Independence", "Burlington"],
    query: "Union KY OR Independence KY OR Burlington KY",
  },
  {
    title: "Close to Cincinnati",
    desc: "Quick access to downtown, riverfront spots, and walkable neighborhoods.",
    pills: ["Covington", "Newport", "Fort Thomas"],
    query: "Covington KY OR Newport KY OR Fort Thomas KY",
  },
  {
    title: "Quiet & Scenic",
    desc: "More privacy, winding roads, and a calmer pace.",
    pills: ["Rabbit Hash", "Verona", "California"],
    query: "Rabbit Hash KY OR Verona KY OR California KY",
  },
  {
    title: "Top-Rated Schools",
    desc: "Areas people often ask about when schools are the top priority.",
    pills: ["Fort Thomas", "Beechwood", "Campbell County"],
    query: "Fort Thomas KY OR Beechwood KY OR Campbell County KY",
  },
];

const ZIP_LOCATIONS = [
  {
    zip: "41001",
    token: "ChIJJ7kKjJ-jQYgRjGCrCZ7Ia8Q-0.5338605285365372,41001,Alexandria%2C%20KY%2041001%2C%20USA",
    viewport: "39.00429889999999,-84.32677,38.8156108,-84.46475389999999",
  },
  {
    zip: "41018",
    token: "ChIJj7-NWebHQYgRaw6TbnUWl94-0.837623887685863,41018,Covington%2C%20KY%2041018%2C%20USA",
    viewport: "39.062084,-84.5511399,38.968371,-84.627859",
  },
  {
    zip: "41073",
    token: "ChIJlTO5zYOxQYgRpdY639jMHUk-0.040949980498967475,41073,Bellevue%2C%20KY%2041073%2C%20USA",
    viewport: "39.1133441,-84.4628599,39.0923141,-84.492672",
  },
  {
    zip: "41005",
    token: "ChIJHSp2LTfFQYgRo_C95m7vyiQ-0.6502030113945678,41005,Rabbit%20Hash%2C%20KY%2041005%2C%20USA",
    viewport: "39.08916,-84.643997,38.89702200000001,-84.87925779999999",
  },
  {
    zip: "41030",
    token: "ChIJM1YBh6aUQYgRURcOo-jlmIU-0.3062171132143874,41030,Crittenden%2C%20KY%2041030%2C%20USA",
    viewport: "38.849602,-84.5189341,38.741107,-84.682948",
  },
  {
    zip: "41074",
    token: "ChIJ9RWJwyCyQYgRZ8VOLeekP48-0.9225937742590447,41074,Bellevue%2C%20KY%2041074%2C%20USA",
    viewport: "39.121762,-84.4409859,39.101937,-84.4848098",
  },
  {
    zip: "41006",
    token: "ChIJ29TJJOueQYgRbsr5rc0pTRw-0.4599821005707101,41006,Butler%2C%20KY%2041006%2C%20USA",
    viewport: "38.872524,-84.2304109,38.6776501,-84.4245519",
  },
  {
    zip: "41033",
    token: "ChIJzYg3qceQQYgRoLQws3koNgQ-0.5778637887628031,41033,Demossville%2C%20KY%2041033%2C%20USA",
    viewport: "38.8365991,-84.37946,38.6983726,-84.5322881",
  },
  {
    zip: "41075",
    token: "ChIJCXb1hsOxQYgRxDRBpigwM7g-0.4060938589552219,41075,Fort%20Thomas%2C%20KY%2041075%2C%20USA",
    viewport: "39.111334,-84.429558,39.046681,-84.4746711",
  },
  {
    zip: "41007",
    token: "ChIJrQ7gxfGgQYgRuiCMWnf8KlU-0.912853996330145,41007,Mentor%2C%20KY%2041007%2C%20USA",
    viewport: "38.980871,-84.231533,38.821397,-84.393875",
  },
  {
    zip: "41042",
    token: "ChIJAw8jsE_GQYgReGj5xkwhMPI-0.10822725542627842,41042,Florence%2C%20KY%2041042%2C%20USA",
    viewport: "39.042674,-84.59214109999999,38.9314539,-84.7082789",
  },
  {
    zip: "41076",
    token: "ChIJ74ZEd1KlQYgRMU7TAZ3-nAk-0.8027466453910259,41076,Highland%20Heights%2C%20KY%2041076%2C%20USA",
    viewport: "39.0871871,-84.36926989999999,38.965027,-84.4937059",
  },
  {
    zip: "41011",
    token: "ChIJvWUtySm3QYgR04ahTiKE5cE-0.919986487451812,41011,Covington%2C%20KY%2041011%2C%20USA",
    viewport: "39.0951771,-84.49733309999999,39.03557,-84.561337",
  },
  {
    zip: "41048",
    token: "ChIJh39M5c_OQYgRxtkgvSlJ604-0.36264060862628944,41048,Hebron%2C%20KY%2041048%2C%20USA",
    viewport: "39.147732,-84.6195509,39.036339,-84.80754200000001",
  },
  {
    zip: "41080",
    token: "ChIJPyN4dpjQQYgRlm0LThExxCU-0.9399024846037081,41080,Petersburg%2C%20KY%2041080%2C%20USA",
    viewport: "39.1099138,-84.775576,38.988796,-84.897336",
  },
  {
    zip: "41014",
    token: "ChIJk2jZVdiwQYgRYTF3eq_o2VU-0.4038620269125779,41014,Covington%2C%20KY%2041014%2C%20USA",
    viewport: "39.077973,-84.4910199,39.051839,-84.5263329",
  },
  {
    zip: "41051",
    token: "ChIJLV17A8G-QYgRH7o2LDPLxkE-0.27657254985338864,41051,Independence%2C%20KY%2041051%2C%20USA",
    viewport: "38.99589,-84.49099609999999,38.865862,-84.6232229",
  },
  {
    zip: "41085",
    token: "ChIJ3fg7lnWvQYgRexMlr0u9lsA-0.966998213530678,41085,Silver%20Grove%2C%20KY%2041085%2C%20USA",
    viewport: "39.0381609,-84.3851931,39.032131,-84.4027883",
  },
  {
    zip: "41015",
    token: "ChIJf-yRXFO6QYgRaJrwaV2Af_U-0.5491074303506074,41015,Covington%2C%20KY%2041015%2C%20USA",
    viewport: "39.0571568,-84.426791,38.9023619,-84.533385",
  },
  {
    zip: "41059",
    token: "ChIJMROnHwqmQYgRhrCuTlgbyYw-0.27600609682830957,41059,Melbourne%2C%20KY%2041059%2C%20USA",
    viewport: "39.0430119,-84.2962379,38.9665831,-84.3962501",
  },
  {
    zip: "41091",
    token: "ChIJj73a3AXDQYgRbosw2mdABXA-0.3772396334133916,41091,Union%2C%20KY%2041091%2C%20USA",
    viewport: "38.99078,-84.62893609999999,38.853885,-84.86586009999999",
  },
  {
    zip: "41016",
    token: "ChIJCzpG0uu2QYgRDD20movoVgo-0.4190432430988742,41016,Covington%2C%20KY%2041016%2C%20USA",
    viewport: "39.09463091959276,-84.53845134116277,39.074077608183245,-84.55986609793767",
    zoom: 15,
  },
  {
    zip: "41063",
    token: "ChIJHZ2gpReWQYgRj8k3sonCP8Q-0.46153699922471825,41063,Morning%20View%2C%20KY%2041063%2C%20USA",
    viewport: "38.9185529,-84.4174401,38.791633,-84.5748085",
  },
  {
    zip: "41092",
    token: "ChIJF8tD6HvpQYgRG8KlEoEUlr4-0.8688776908172422,41092,Verona%2C%20KY%2041092%2C%20USA",
    viewport: "38.860855,-84.615731,38.770216,-84.7801109",
  },
  {
    zip: "41017",
    token: "ChIJaxmWmdS5QYgRGktNXR5nUCQ-0.18065075265518904,41017,Covington%2C%20KY%2041017%2C%20USA",
    viewport: "39.04789002350438,-84.54463247139799,39.02732309835724,-84.56604722817289",
    zoom: 15,
  },
  {
    zip: "41071",
    token: "ChIJPe4lWgexQYgRBD7Hi612L60-0.7805963977816677,41071,Fort%20Thomas%2C%20KY%2041071%2C%20USA",
    viewport: "39.1042518,-84.454765,39.015339,-84.50616079999999",
  },
  {
    zip: "41094",
    token: "ChIJLxX4kDXAQYgRQqJk4JKjmkQ-0.8896056480160008,41094,Walton%2C%20KY%2041094%2C%20USA",
    viewport: "38.9472289,-84.5458288,38.8182369,-84.743849",
  },
];

const SCHOOL_DISTRICTS = [
  {
    name: "Beechwood Independent",
    slug: "Beechwood-Independent-School-District-Fort-Mitchell-KY-USA",
    id: "918753",
    q: "Beechwood Independent School District, Fort Mitchell, KY, USA",
    viewport: "39.0668247120808,-84.52153201562498,39.02536250700113,-84.58744998437498",
  },
  {
    name: "Boone County",
    slug: "Boone-County-Schools-Burlington-KY-USA",
    id: "917721",
    q: "Boone County Schools, Burlington, KY, USA",
    viewport: "39.31077093829062,-84.22152314999998,38.646752318467335,-85.27621064999998",
  },
  {
    name: "Campbell County",
    slug: "Campbell-County-Schools-Alexandria-KY-USA",
    id: "917738",
    q: "Campbell County Schools, Alexandria, KY, USA",
    viewport: "39.12984397875356,-84.102559325,38.797761320224204,-84.629903075",
  },
  {
    name: "Kenton County",
    slug: "Kenton-County-Schools-Independence-KY-USA",
    id: "918656",
    q: "Kenton County Schools, Independence, KY, USA",
    viewport: "39.10293629394357,-84.25685752499999,38.770727248655845,-84.78420127499999",
  },
  {
    name: "Covington Independent",
    slug: "Covington-Independent-School-District-Covington-KY-USA",
    id: "918806",
    q: "Covington Independent School District, Covington, KY, USA",
    viewport: "39.106648503534004,-84.45232143125,39.02374653726171,-84.58415736875",
  },
  {
    name: "Fort Thomas Independent",
    slug: "Fort-Thomas-Independent-School-District-Fort-Thomas-KY-USA",
    id: "918822",
    q: "Fort Thomas Independent School District, Fort Thomas, KY, USA",
    viewport: "39.12296676293923,-84.38383308124999,39.04008397570846,-84.51566901874999",
  },
  {
    name: "Walton-Verona Independent",
    slug: "Walton-Verona-Independent-School-District-Walton-KY-USA",
    id: "918900",
    q: "Walton Verona Independent School District, Walton, KY, USA",
    viewport: "38.91829070322489,-84.5255580625,38.75194779466644,-84.7892299375",
  },
  {
    name: "Erlanger-Elsmere Independent",
    slug: "ErlangerElsmere-Independent-School-District-Erlanger-KY-USA",
    id: "917856",
    q: "Erlanger-Elsmere Independent School District, Erlanger, KY, USA",
    viewport: "39.06038265448146,-84.53312873125002,38.977426348044915,-84.66496466875002",
  },
  {
    name: "Newport Independent",
    slug: "Newport-Independent-School-District-Newport-KY-USA",
    id: "918858",
    q: "Newport Independent School District, Newport, KY, USA",
    viewport: "39.10509708695424,-84.45086121562498,39.063657362324456,-84.51677918437498",
  },
];



export default function Neighborhoods() {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const scrollToId = useCallback((id, offset = 132) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  const qLower = useMemo(() => query.trim().toLowerCase(), [query]);

  const filteredCities = useMemo(() => {
    if (!qLower) return FEATURED_CITIES;
    return FEATURED_CITIES.filter((c) => c.name.toLowerCase().includes(qLower));
  }, [qLower]);

  /**
   * ✅ One universal search function used everywhere:
   * - search bar
   * - city cards
   * - lifestyle bucket cards
   * - lifestyle pills
   *
   * ZIP + districts still use their special KW URLs.
   * Everything else uses /search/sale?q=...&viewport=...
   */
  const openUniversalPlaceSearch = useCallback(async (placeText) => {
    const place = normalizeQuery(placeText);
    if (!place) return openNewTab(buildKwSearchUrl("Northern Kentucky"));

    setIsSearching(true);
    try {
      // 1) ZIP exact match -> KW location token URL (your curated tokens)
      if (/^\d{5}$/.test(place)) {
        const zipHit = ZIP_LOCATIONS.find((z) => z.zip === place);
        if (zipHit) return openNewTab(buildZipLocationUrl(zipHit));
      }

      const placeLower = place.toLowerCase();

      // 2) School district contains match -> entity URL (your curated entities)
      const districtHit = SCHOOL_DISTRICTS.find((d) => d.name.toLowerCase().includes(placeLower));
      if (districtHit) {
        return openNewTab(
          districtHit.slug && districtHit.id
            ? buildKwEntityUrl({
                slug: districtHit.slug,
                id: districtHit.id,
                q: districtHit.q,
                viewport: districtHit.viewport,
              })
            : buildKwSearchUrl(districtHit.qFallback || districtHit.name)
        );
      }

      // 3) Universal: viewport lookup (backend preferred, geocode fallback)
      const backend = await fetchKwViewport(place);
      let viewport = backend?.viewport || null;
      let finalQ = backend?.q || null;

      if (!viewport) {
        const geo = await getViewportFromGeocode(place);
        viewport = geo?.viewport || null;
        finalQ = geo?.q || finalQ || place;
      }

      // 4) Universal KW sale URL
      return openNewTab(buildKwSearchUrl(finalQ || place, viewport));
    } finally {
      setIsSearching(false);
    }
  }, []);

  const onSubmitSearch = useCallback(() => {
    openUniversalPlaceSearch(query);
  }, [query, openUniversalPlaceSearch]);

  const resultsLabel = useMemo(() => {
    const q = query.trim();
    if (!q) return "Search by city, ZIP, school district, or keyword — or browse below.";

    const qL = q.toLowerCase();
    const cityMatches = FEATURED_CITIES.filter((c) => c.name.toLowerCase().includes(qL)).length;
    const districtMatches = SCHOOL_DISTRICTS.filter((d) => d.name.toLowerCase().includes(qL)).length;
    const zipExact = /^\d{5}$/.test(q) && ZIP_LOCATIONS.some((z) => z.zip === q);

    if (zipExact) return `ZIP match: ${q}`;
    if (districtMatches || cityMatches) {
      const parts = [];
      if (districtMatches) parts.push(`${districtMatches} school match${districtMatches === 1 ? "" : "es"}`);
      if (cityMatches) parts.push(`${cityMatches} featured cit${cityMatches === 1 ? "y" : "ies"}`);
      return `${parts.join(" • ")} • Press Enter to search`;
    }

    return `Press Enter to search KW for “${q}”`;
  }, [query]);

  return (
    <div className="landing-container">
      <Navbar variant="neighborhoods" />

      {/* HERO */}
      <section className="nb-hero" id="search">
        {/* Background media */}
        <div className="hero-media" aria-hidden="true">
          <video
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/imgs/aerial.jpg"
          >
            <source src="/imgs/birdseyeview.mp4" type="video/mp4" />
          </video>

          <img className="hero-image" src="/imgs/aerial.jpg" alt="" loading="eager" decoding="async" />
          <div className="hero-overlay" />
        </div>

        {/* Foreground content */}
        <div className="nb-hero-inner">
          <h1 className="nb-hero-title">Browse Homes</h1>

          <p className="nb-hero-subtitle">
            Browse communities by lifestyle, location, and schools — with local insight to help you narrow your search
            quickly and confidently.
          </p>

          {/* Search row */}
          <div className="nb-search">
            <div className="nb-search-field">
              <span className="nb-search-icon" aria-hidden="true">
                ⌕
              </span>

              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSubmitSearch();
                }}
                placeholder="Search city, ZIP, school district, or keyword (ex: 41071, Beechwood, Florence)"
                type="text"
                disabled={isSearching}
              />
            </div>

            <div className="nb-search-actions">
              <button type="button" className="nb-search-cta" onClick={onSubmitSearch} disabled={isSearching}>
                {isSearching ? "Searching..." : "Search"}
              </button>

              <button
                type="button"
                className="nb-search-cta secondary"
                onClick={() => scrollToId("cities")}
                disabled={isSearching}
              >
                Browse Cities
              </button>
            </div>
          </div>
        </div>

        <div className="nb-hero-fade" aria-hidden="true" />
      </section>

      {/* LIFESTYLE */}
      <section className="nb-section" id="lifestyle">
        <div className="nb-inner">
          <div className="nb-section-head">
            <h2>Browse by Lifestyle</h2>
            <p>Tap a card to search listings, or choose a suggested area.</p>
          </div>

          <div className="nb-grid">
            {LIFESTYLE_BUCKETS.map((b) => (
              <div key={b.title} className="nb-card" role="group" aria-label={b.title}>
                <button type="button" className="nb-card-button" onClick={() => openUniversalPlaceSearch(b.query)}>
                  <div className="nb-card-top">
                    <h3>{b.title}</h3>
                    <span className="nb-card-arrow" aria-hidden="true">
                      →
                    </span>
                  </div>
                  <p>{b.desc}</p>
                </button>

                <div className="nb-pills">
                  {b.pills.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="nb-pill"
                      onClick={() => openUniversalPlaceSearch(p)}
                      aria-label={`Search homes in ${p}`}
                      disabled={isSearching}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="nb-section nb-section--alt" id="cities">
        <div className="nb-inner">
          <div className="nb-section-head">
            <h2>Featured Cities</h2>
            <p>Curated picks people ask about most — clean, simple, and useful.</p>
          </div>

          {/* ✅ City cards use universal search (no KW slug/id needed) */}
          <div className="nb-city-grid">
            {filteredCities.map((c) => (
              <button
                type="button"
                className="nb-city"
                key={c.name}
                onClick={() => openUniversalPlaceSearch(`${c.name}, KY, USA`)}
                aria-label={`View homes in ${c.name}`}
                disabled={isSearching}
              >
                <div className="nb-city-tag">{c.tag}</div>
                <div className="nb-city-name">{c.name}</div>
                <div className="nb-city-blurb">{c.blurb}</div>
                <div className="nb-city-link">View homes →</div>
              </button>
            ))}
          </div>

          <div className="nb-note">
            Don’t see what you’re looking for?{" "}
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                window.location.href = "/contact";
              }}
            >
              Let's Talk
            </button>{" "}
          </div>
        </div>
      </section>

      {/* SCHOOLS */}
      <section className="nb-section" id="districts">
        <div className="nb-inner">
          <div className="nb-section-head">
            <h2>Browse by School District</h2>
            <p>Use this if schools are your top priority — simple and scannable.</p>
          </div>

          <div className="nb-list">
            {SCHOOL_DISTRICTS.map((d) => {
              const href =
                d.slug && d.id
                  ? buildKwEntityUrl({
                      slug: d.slug,
                      id: d.id,
                      q: d.q,
                      viewport: d.viewport,
                    })
                  : buildKwSearchUrl(d.qFallback || d.name);

              return (
                <a className="nb-list-item" key={d.name} href={href} target="_blank" rel="noreferrer">
                  <span>{d.name}</span>
                  <span className="nb-list-arrow" aria-hidden="true">
                    →
                  </span>
                </a>
              );
            })}
          </div>

          <div className="nb-disclaimer">
            School boundaries are approximations and can change. Always confirm with the district.
          </div>
        </div>
      </section>

      {/* ADVANCED */}
<section className="nb-section nb-section--alt" id="zipcodes">
  <div className="nb-inner">
    <div className="nb-section-head">
      <h2>Advanced</h2>
      <p>If you know exactly what you need, use these quick shortcuts.</p>
    </div>

    <div className="nb-advanced">
      {/* ZIP CODES */}
      <div className="nb-advanced-block">
        <h3>Zip Codes</h3>

        <div className="nb-chip-grid">
          {ZIP_LOCATIONS.map((z) => (
            <a
              key={z.zip}
              className="nb-chip"
              href={buildZipLocationUrl(z)}
              target="_blank"
              rel="noreferrer"
            >
              {z.zip}
            </a>
          ))}
        </div>
      </div>
    </div>
  </div>
</section>
    </div>
  );
}
